import { documentFetch } from "@/lib/document/docClient";
import type {
  DocDocument,
  DocDocumentGetResponse,
  DocDocumentListResponse,
  DocVersionGetResponse,
  DocVersionPublishResponse,
  DocVersionRestoreResponse,
  DocVersionSaveResponse,
} from "@/lib/document/documentTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { normalizeDocBlocks } from "@/lib/document-builder/blocks/blockNormalize";
import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import { parseVariablesSnapshot } from "@/lib/document-builder/variables/variableFill";

export function upsertDocDocument(body: {
  id?: string;
  name: string;
  category_id: string;
  description?: string | null;
  tags?: string[];
}) {
  return documentFetch<{ ok: true; document: DocDocument }>("/api/doc/document-upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getDocDocument(documentId: string) {
  const q = new URLSearchParams({ document_id: documentId });
  return documentFetch<DocDocumentGetResponse>(`/api/doc/document-get?${q.toString()}`, {
    method: "GET",
  });
}

export function getDocVersion(args: { versionId?: string; documentId?: string; draft?: boolean }) {
  const q = new URLSearchParams();
  if (args.versionId) q.set("version_id", args.versionId);
  if (args.documentId) q.set("document_id", args.documentId);
  if (args.draft) q.set("draft", "1");
  return documentFetch<DocVersionGetResponse>(`/api/doc/version-get?${q.toString()}`, {
    method: "GET",
  });
}

export function saveDocVersion(body: {
  document_id: string;
  blocks: DocBlock[];
  variables?: DocVariableSnapshot[];
  change_notes?: string | null;
}) {
  return documentFetch<DocVersionSaveResponse>("/api/doc/version-save", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function parseBlocksSnapshot(raw: unknown): DocBlock[] {
  if (!Array.isArray(raw)) return [];
  return normalizeDocBlocks(raw as DocBlock[]);
}

export { parseVariablesSnapshot };

export function listDocDocuments(args?: { categoryId?: string; publishedOnly?: boolean }) {
  const q = new URLSearchParams();
  if (args?.categoryId) q.set("category_id", args.categoryId);
  if (args?.publishedOnly) q.set("published_only", "1");
  const suffix = q.toString();
  return documentFetch<DocDocumentListResponse>(
    suffix ? `/api/doc/document-list?${suffix}` : "/api/doc/document-list",
    { method: "GET" },
  );
}

export function publishDocVersion(body: { document_id: string; version_id?: string }) {
  return documentFetch<DocVersionPublishResponse>("/api/doc/version-publish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function restoreDocVersion(body: {
  document_id: string;
  version_id: string;
  change_notes?: string | null;
}) {
  return documentFetch<DocVersionRestoreResponse>("/api/doc/version-restore", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
